// Plan comptable propre à la société (13/10/2026) : import du plan de
// l'expert-comptable (« numéro;intitulé »), aperçu puis import. Comptes
// généraux seulement (sans 401/411) ; aucun compte existant modifié.
// « + Nouveau compte » (26/10/2026) : un compte général à l'unité.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const CLASS_LABELS = { 1: "Capitaux", 2: "Immobilisations", 3: "Stocks", 4: "Tiers", 5: "Financiers", 6: "Charges", 7: "Produits" };

const toBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result).split(",")[1] || "");
  r.onerror = () => reject(new Error("Lecture du fichier impossible."));
  r.readAsDataURL(file);
});

export default function CompanyChartTab({ companyId }) {
  const { user } = useAuth();
  const canEdit = ["admin", "comptable"].includes(user?.role);
  const [newAccount, setNewAccount] = useState(null); // { account_code, label } pendant la saisie
  const [createdAccount, setCreatedAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState("");
  const [payload, setPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (companyId) api.chartAccounts.list(companyId).then(setAccounts).catch(e => setError(e.message));
  }, [companyId]);
  useEffect(() => { load(); }, [load]);

  const choose = async (file) => {
    if (!file) return;
    setBusy(true); setError(""); setPreview(null); setDone(null);
    try {
      const data = { filename: file.name, content_base64: await toBase64(file) };
      setPayload(data);
      setPreview(await api.chartAccounts.import(companyId, { ...data, dry_run: true }));
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setError("");
    try { setDone(await api.chartAccounts.import(companyId, { ...payload, dry_run: false })); setPreview(null); load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const create = async () => {
    setBusy(true); setError(""); setCreatedAccount(null);
    try { setCreatedAccount(await api.chartAccounts.create(companyId, newAccount)); setNewAccount(null); load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const newClass = newAccount && /^[1-7]/.test(newAccount.account_code) ? newAccount.account_code[0] : null;
  const shown = q ? accounts.filter(a => a.account_code.startsWith(q) || a.label.toLowerCase().includes(q)) : accounts;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <div style={{ fontSize: 13, color: "#4b5563" }}>
          {accounts.length ? `${accounts.length} comptes généraux importés.` : "Aucun plan importé : les suggestions de comptes reprennent le PCG."} Les suggestions de comptes d'achat sont prises dans ce plan dès qu'il est importé.
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {accounts.length > 0 && <button className="btn" disabled={busy} onClick={() => { setNewAccount(newAccount ? null : { account_code: "", label: "" }); setError(""); setCreatedAccount(null); }}>+ Nouveau compte</button>}
            <label className="btn" style={{ cursor: busy ? "wait" : "pointer" }}>
              Importer le plan de l'expert-comptable
              <input type="file" accept=".txt,.csv" style={{ display: "none" }} disabled={busy} onChange={e => { choose(e.target.files[0]); e.target.value = ""; }} />
            </label>
          </div>
        )}
      </div>
      {newAccount && (
        <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Nouveau compte général</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input className="input" placeholder="Numéro (ex. 627800)" inputMode="numeric" value={newAccount.account_code} onChange={e => setNewAccount(a => ({ ...a, account_code: e.target.value.replace(/\s/g, "") }))} style={{ width: 160 }} />
            <input className="input" placeholder="Intitulé" maxLength={100} value={newAccount.label} onChange={e => setNewAccount(a => ({ ...a, label: e.target.value }))} style={{ flex: 1, minWidth: 200 }} />
            <button className="btn primary" disabled={busy || !newAccount.account_code || !newAccount.label.trim()} onClick={create}>Créer le compte</button>
          </div>
          <div style={{ color: "#4b5563", marginTop: 6 }}>
            {newClass ? `Classe ${newClass} — ${CLASS_LABELS[newClass]}. ` : ""}Comptes 401 et 411 exclus : ils sont créés avec chaque fournisseur ou client.
          </div>
        </div>
      )}
      {createdAccount && <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>✓ Compte {createdAccount.account_code} « {createdAccount.label} » créé (classe {createdAccount.class} — {createdAccount.class_label}).</div>}
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 10, fontSize: 13 }}>{error}</div>}
      {preview && (
        <div style={{ background: "#f0fdf9", borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Aperçu — rien n'est encore importé</div>
          <div>{preview.general_accounts} comptes généraux lus · <strong>{preview.to_create} à créer</strong> · {preview.already_present} déjà présents · comptes auxiliaires écartés : {preview.excluded.auxiliaires}</div>
          {preview.different_label_kept.length > 0 && <div>Déjà présents avec un autre intitulé (non modifiés) : {preview.different_label_kept.join(", ")}</div>}
          {preview.merged_forms > 0 && <div>{preview.merged_forms} comptes présents sous deux formes (ex. 445714 et 4457140000000) : un seul compte créé.</div>}
          {preview.used_not_in_plan.length > 0 && <div style={{ color: "#92400e" }}>⚠️ Comptes déjà utilisés par l'application et absents du plan : {preview.used_not_in_plan.map(u => `${u.account_code} (${u.lines})`).join(", ")} — à faire correspondre avec l'expert-comptable.</div>}
          <button className="btn primary" style={{ marginTop: 8 }} disabled={busy || !preview.to_create} onClick={confirm}>Importer {preview.to_create} compte(s)</button>
        </div>
      )}
      {done && <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 13 }}>✓ {done.created} compte(s) importé(s).</div>}
      {accounts.length > 0 && (
        <>
          <input className="input" placeholder="Rechercher un compte (numéro ou intitulé)" value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 8, width: "100%", maxWidth: 360 }} />
          <table className="table" style={{ width: "100%", fontSize: 12 }}>
            <thead><tr><th style={{ textAlign: "left" }}>Compte</th><th style={{ textAlign: "left" }}>Intitulé</th></tr></thead>
            <tbody>{shown.slice(0, 500).map(a => <tr key={a.account_code}><td>{a.account_code}</td><td>{a.label}</td></tr>)}</tbody>
          </table>
        </>
      )}
    </div>
  );
}
